import { type FC, useMemo } from 'react'
import { Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { GRAY_700 } from 'constants/styling/theme'
import { SelectableButtonsGrid } from 'components/common/SelectableButtonsGrid'
import type { ToggleFilterValue } from 'components/common/ToggleFilter'

enum ToggleOptions {
  On = 'on',
  Off = 'off',
}

interface FloorPlanToggleProps {
  /** Section title. */
  title: string
  /** Whether the toggle is currently active. */
  value: boolean
  /** Called with the new boolean when the toggle is changed. */
  onChange: (value: boolean) => void
}

/**
 * @component FloorPlanToggleSetting
 * A binary on/off toggle control for a floor-plan setting.
 */
export const FloorPlanToggleSetting: FC<FloorPlanToggleProps> = ({ title, value, onChange }) => {
  const { t } = useTranslation(['purchase_flow'])

  const floorplanToggleValues: ToggleFilterValue<ToggleOptions>[] = useMemo(
    () =>
      [ToggleOptions.Off, ToggleOptions.On].map((option) => ({
        value: option,
        displayName: t(`floor_plan_step.layout.${option}`),
      })),
    [t]
  )

  return (
    <Stack gap=".6rem">

      <Typography variant="text-sm" fontWeight={500} color={GRAY_700}>
        {title}
      </Typography>

      <SelectableButtonsGrid
        activeButtonClickable
        values={floorplanToggleValues}
        selectedValue={value ? ToggleOptions.On : ToggleOptions.Off}
        onSelect={(option) => onChange(option === ToggleOptions.On)}
      />

    </Stack>
  )
}
